import { useRouter } from "@uirouter/react";
import { useEffect } from "react";
import { fromEvent, of } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  skip,
  startWith,
  switchMap,
} from "rxjs/operators";
import { AppStore, store } from "..";
import { store$ } from "../utils";
import { DashboardRoutes } from "../../pages/dashboard/routes";
import { LoginRoutes } from "../../pages/login/routes";
import { useCurrentStateAndParams } from "../../router";
import { isFetchableSuccess } from "../common/fetchable/helpers";
import { NotificationActions } from "../notifications/actions";
import { AuthActions } from "./actions";
import { AuthService } from "./service";
import { useSelector } from "react-redux";

const tenMin = 10 * 60 * 1000;

export const useAuthEffects = () => {
  const router = useRouter();
  const redirectUrl = useSelector<AppStore, string | undefined>(
    (s) => s.auth.redirectUrl
  );
  const currentState = useCurrentStateAndParams();

  // Here we listen to logging in or out and redirect user
  useEffect(() => {
    const sub = store$
      .pipe(
        map((x) => {
          if (!isFetchableSuccess(x.auth.login)) return "LOGGED_OUT";
          else {
            return x.auth.login.data.accessToken.authenticated
              ? x.auth.login.data.accessToken
              : "2FA";
          }
        }),
        distinctUntilChanged(
          (prev, curr) =>
            typeof prev === typeof curr &&
            JSON.stringify(prev) === JSON.stringify(curr)
        ),
        map((x) => (typeof x !== "string" ? "LOGGED_IN" : x)),
        skip(1),
        filter((x) => {
          return (
            x === "2FA" ||
            (x === "LOGGED_IN" && currentState.state.data?.unauthed) ||
            currentState.state.data?.authed
          );
        })
      )
      .subscribe((status) => {
        const state: string = (() => {
          //TODO us StateOrName
          switch (status) {
            case "2FA":
              return LoginRoutes.names.TwoFA_index;
            case "LOGGED_IN":
              return DashboardRoutes.name;
            default:
              return LoginRoutes.names.LOGIN;
          }
        })();

        if (status === "LOGGED_IN" && redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          router.stateService.go(state);
        }
      });
    return () => sub.unsubscribe();
  }, [router.stateService, currentState, redirectUrl]);

  /**
   * Effect to write/clear login from redux store to local storage
   */
  useEffect(() => {
    const sub = store$
      .pipe(
        map((x) => x.auth.login),
        distinctUntilChanged()
      )
      .subscribe((token) =>
        isFetchableSuccess(token)
          ? AuthService.setLogin(token.data)
          : AuthService.clearLogin()
      );
    return () => sub.unsubscribe();
  }, [router.stateService]);

  useEffect(() => {
    const subscription = fromEvent(document, "click")
      .pipe(
        mapTo(undefined),
        startWith(undefined),
        debounceTime(tenMin),
        switchMap(() => of(store.getState().auth.login)),
        filter((login) => isFetchableSuccess(login))
      )
      .subscribe(() => {
        store.dispatch(
          NotificationActions.setNotification({
            title: "you-have-been-logged-out-due-to-inactivity",
            variant: "error",
          })
        );
        store.dispatch(AuthActions.clearAuthState());
      });
    return subscription.unsubscribe;
  }, []);
};
